import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { PorangeButton } from "@/components/atoms/PorangeButton";

export const Statement = ({ onAnswer }) => {
    const [showFirstLine, setShowFirstLine] = useState(false);
    const [showSecondLine, setShowSecondLine] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [bellVisible, setBellVisible] = useState(false);
    const [bellRung, setBellRung] = useState(false);
    const [showUpdateEffect, setShowUpdateEffect] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    const outdatedInfo = [
        { emoji: "📱", text: "Old phone number", status: "❌" },
        { emoji: "🕐", text: "Closed at 6pm", status: "❌" },
        { emoji: "🍽️", text: "Old menu", status: "❌" },
    ];

    const updatedInfo = [
        { emoji: "📱", text: "New contact info", status: "✅" },
        { emoji: "🕙", text: "Open until 9pm", status: "✅" },
        { emoji: "📝", text: "Updated menu", status: "✅" },
    ];

    useEffect(() => {
        // Text animations
        setTimeout(() => setShowFirstLine(true), 200);
        setTimeout(() => setShowSecondLine(true), 600);

        // Wait 1 second after H1 and H2 load in
        setTimeout(() => {
            setShowAnimation(true);
        }, 1600); // H1 at 200ms, H2 at 600ms, then wait until 1600ms

        // Bell appears smoothly after animation starts
        setTimeout(() => {
            setBellVisible(true);
        }, 1800); // 200ms after animation container appears

        // Bell ring animation
        setTimeout(() => {
            setBellRung(true); // Start bell ring animation
        }, 2400); // 600ms after bell becomes visible

        // Start updating info
        setTimeout(() => {
            setShowUpdateEffect(true);
        }, 3000); // Start update effect 600ms after bell rings

        // Show continue button
        setTimeout(() => setShowButton(true), 4500);
    }, []);

    const handleContinue = () => {
        setShowButton(false);
        setIsExiting(true);
        setTimeout(() => onAnswer('continue'), 500);
    };

    return (
        <div
            className={classNames(
                "relative h-full flex flex-col items-center justify-start px-6",
                "transition-all duration-500 ease-in-out",
                isExiting
                    ? "opacity-0 transform translate-y-10"
                    : "opacity-100 transform translate-y-0"
            )}
        >
            <div className="text-center space-y-4 max-w-[600px] mt-[48px]">
                <div className="overflow-hidden">
                    <h2
                        className={classNames(
                            "text-[32px] md:text-[40px] leading-tight font-bold transition-all duration-500 text-black",
                            showFirstLine ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"
                        )}
                    >
                        Real-time{' '}
                        <span className="animate-shimmer bg-[length:200%_100%] bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-orange-500 to-pink-500">
                            Updates
                        </span>
                    </h2>
                </div>

                <div className="overflow-hidden">
                    <p
                        className={classNames(
                            "text-xl md:text-2xl text-gray-600 transition-all duration-500",
                            showSecondLine ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"
                        )}
                    >
                        Our team works with the community to keep information accurate and up-to-date
                    </p>
                </div>
            </div>

            {showAnimation && (
                <div
                    className={classNames(
                        "relative mt-12 mb-24 transition-all duration-500",
                        isExiting ? "opacity-0 transform translate-y-10" : "opacity-100 transform translate-y-0"
                    )}
                >
                    <div className="flex items-center justify-center">
                        {/* Notification Bell */}
                        {bellVisible && (
                            <div
                                className={classNames(
                                    "transition-opacity duration-500",
                                    bellVisible ? "opacity-100" : "opacity-0"
                                )}
                            >
                                <div className="w-16 h-16 flex items-center justify-center">
                                    <span
                                        className={classNames(
                                            "text-5xl",
                                            bellRung ? "animate-bell-ring" : "animate-bell-idle"
                                        )}
                                    >
                                        🔔
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Information Cards */}
                        <div className="relative ml-4">
                            {/* Outdated Info */}
                            <div
                                className={classNames(
                                    "transition-opacity duration-500",
                                    showUpdateEffect ? "opacity-0" : "opacity-100"
                                )}
                            >
                                {outdatedInfo.map((item, index) => (
                                    <div
                                        key={`old-${index}`}
                                        className={classNames(
                                            "flex items-center justify-center mb-2 transition-transform duration-500",
                                            bellVisible
                                                ? "translate-y-0 opacity-100"
                                                : "translate-y-10 opacity-0"
                                        )}
                                        style={{ transitionDelay: `${index * 150}ms` }}
                                    >
                                        <div className="bg-gray-100 rounded-xl p-2 flex items-center space-x-2 min-w-[200px]">
                                            <span className="text-xl">{item.emoji}</span>
                                            <span className="text-gray-500 flex-1 text-sm">{item.text}</span>
                                            <span className="text-lg">{item.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Updated Info */}
                            <div
                                className={classNames(
                                    "absolute inset-0 transition-opacity duration-500",
                                    showUpdateEffect ? "opacity-100" : "opacity-0"
                                )}
                            >
                                {updatedInfo.map((item, index) => (
                                    <div
                                        key={`new-${index}`}
                                        className="flex items-center justify-center mb-2 transition-transform duration-500"
                                        style={{ transitionDelay: `${index * 150}ms` }}
                                    >
                                        <div className="bg-green-50 rounded-xl p-2 flex items-center space-x-2 min-w-[200px]">
                                            <span className="text-xl">{item.emoji}</span>
                                            <span className="text-gray-700 flex-1 text-sm">{item.text}</span>
                                            <span className="text-lg">{item.status}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div
                className={classNames(
                    "fixed bottom-0 left-0 right-0 p-5 transition-all duration-500 bg-gradient-to-t from-white via-white to-transparent pb-6 pt-10",
                    showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"
                )}
            >
                <PorangeButton
                    full={true}
                    className="h-[56px] rounded-[16px] text-lg shadow-md"
                    onClick={handleContinue}
                >
                    Continue
                </PorangeButton>
            </div>

            <style jsx global>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }

                @keyframes bell-ring {
                    0% { transform: rotate(0deg); }
                    15% { transform: rotate(-15deg); }
                    30% { transform: rotate(15deg); }
                    45% { transform: rotate(-10deg); }
                    60% { transform: rotate(10deg); }
                    75% { transform: rotate(-5deg); }
                    90% { transform: rotate(5deg); }
                    100% { transform: rotate(0deg); }
                }

                @keyframes bell-idle {
                    0%, 100% { transform: rotate(0deg); }
                    50% { transform: rotate(0deg); }
                }

                .animate-shimmer {
                    animation: shimmer 3s linear infinite;
                }

                .animate-bell-ring {
                    animation: bell-ring 1s ease-in-out forwards;
                }

                .animate-bell-idle {
                    animation: bell-idle 2s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

export default Statement;